import * as React from "react";


type Phase = { number: string; name: string };
type ProgressRow = {
  label: string;
  completed: number;
  total: number;
  figures: string;
};
type StreamEntry = { time: string; text: string };
type StreamGroup = { heading: string; entries: StreamEntry[] };

const PHASES: Phase[] = [
  { number: "01", name: "Launch" },
  { number: "02", name: "Follow-up" },
  { number: "03", name: "Term Sheets and Process" },
  { number: "04", name: "Due Diligence" },
  { number: "05", name: "Close" },
];

const PROGRESS_ROWS: ProgressRow[] = [
  { label: "Initial outreach", completed: 200, total: 500, figures: "200 sent / 300 queued / 500 total" },
  { label: "LinkedIn messages", completed: 50, total: 100, figures: "50 sent / 50 queued" },
  { label: "Calls/Meetings", completed: 5, total: 9, figures: "5 occurred / 4 upcoming" },
];

const STREAM_GROUPS: StreamGroup[] = [
  {
    heading: "Check 4:00 AM: 3 queued",
    entries: [
      { time: "4:00 AM", text: "Queued email campaign: send next 40 first new outreach emails. Already approved to send." },
      { time: "4:00 AM", text: "Queued follow-up to Awaiting Response from the Day 1 campaign: send next 30 bespoke follow-up emails using signals. Already approved to send." },
      { time: "4:00 AM", text: "Queued warm intros to investors from founders in the same sector: send 2 emails. Awaiting your approval." },
    ],
  },
  {
    heading: "Between checks",
    entries: [
      { time: "7:35 AM", text: "Follow-up for Blake Silo, Managing Partner, Silo Capital: replied. Blake responded with clear interest and asked for VDR access. Email drafted, awaiting your confirmation to send." },
      { time: "7:47 AM", text: "Follow-up for Dana Whitfield, Managing Director, Guide Asset Management: replied. Dana wants to meet and proposed Thursday 2:00 PM. Your calendar is free then. Confirmation drafted with a Teams invite, awaiting your confirmation." },
      { time: "7:52 AM", text: "Warm intros approved by you. 2 emails sent." },
    ],
  },
  {
    heading: "Check 8:00 AM: 72 sent",
    entries: [
      { time: "8:00 AM", text: "Sent 40 first outreach emails and 30 bespoke follow-ups. 0 bounces." },
      { time: "8:00 AM", text: "Signals: 3 target firms had news overnight (fund close, senior hire, portfolio exit). Their follow-ups were rewritten to reference it before sending." },
      { time: "8:00 AM", text: "LinkedIn: 10 messages prepared for you to send. Open the queue." },
    ],
  },
  {
    heading: "Between checks",
    entries: [
      { time: "9:10 AM", text: "Blake Silo follow-up confirmed by you. Sent with VDR link." },
      { time: "9:41 AM", text: "Reply from Priya Natarajan, Partner, Northlake Growth: not now, revisit next quarter. Marked Not now. Follow-up scheduled for December." },
      { time: "11:15 AM", text: "Dana Whitfield confirmed by you. Teams meeting booked for Thursday 2:00 PM, invite sent." },
      { time: "11:52 AM", text: "Two contacts at Harbor Crest Capital bounced. Replaced with the next right person at the firm. Resend queued for 4:00 AM." },
    ],
  },
  {
    heading: "Check 12:00 PM: 6 replies today",
    entries: [
      { time: "12:00 PM", text: "Replies: 3 interested, 1 meeting request, 1 not now, 1 referral. Two follow-ups already sent, one awaiting you." },
      { time: "12:00 PM", text: "Referral from Elena Ross, Partner, Cobalt Ventures, to a colleague at another firm. New contact added, intro email drafted, awaiting your approval." },
    ],
  },
  {
    heading: "Between checks",
    entries: [
      { time: "2:30 PM", text: "Call with Marcus Hale, Principal, Ridgeline Partners: occurred. Tomorrow morning AgentDart asks how it went and drafts the follow-up." },
      { time: "3:20 PM", text: "Follow-up to Sam Ortega, Vice President, Meridian Credit: held. Daily reply-driven send limit reached (5). Resumes at 4:00 AM." },
    ],
  },
  {
    heading: "Week 1 analysis",
    entries: [
      { time: "4:00 PM", text: "Sent: 200 first outreach, 120 follow-ups, 8 warm intros. 22 replies, an 11 percent reply rate, ahead of the 6 to 8 percent typical for cold institutional outreach." },
      { time: "4:00 PM", text: "What is working: follow-ups that cite a signal (fund close, new hire, exit) reply at twice the rate of plain follow-ups. Growth equity firms with a recent fund close are the most responsive segment, 7 of 12 interested replies. Founder warm intros: 8 sent, 4 replies, 2 meetings." },
      { time: "4:00 PM", text: "What is not: VCs are quiet, 1 reply from 40. Recommendation: pause that segment and reallocate next week's 200 to more growth equity and strategics. Awaiting your approval." },
      { time: "4:00 PM", text: "Meetings: 5 held, 4 booked, two firms have asked for the VDR. Your time this week: 48 minutes across 14 confirmations." },
    ],
  },
];

function useReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return reduced;
}

function EmphasizedText({ text }: { text: string }) {
  const colon = text.indexOf(":");
  if (colon < 0) return <>{text}</>;
  return <><strong>{text.slice(0, colon + 1)}</strong>{text.slice(colon + 1)}</>;
}

function ActionStream({ paused, reduced }: { paused: boolean; reduced: boolean }) {
  const streamRef = React.useRef<HTMLDivElement | null>(null);
  const manualPauseUntil = React.useRef(0);
  const lastAutoTop = React.useRef(0);
  const bottomSince = React.useRef<number | null>(null);
  const lastFrame = React.useRef<number | null>(null);
  const restartingRef = React.useRef(false);
  const [restarting, setRestarting] = React.useState(false);

  React.useEffect(() => {
    const stream = streamRef.current;
    if (!stream || reduced) return;
    const onScroll = () => {
      if (Math.abs(stream.scrollTop - lastAutoTop.current) > 2) {
        manualPauseUntil.current = performance.now() + 5000;
      }
    };
    stream.addEventListener("scroll", onScroll, { passive: true });
    return () => stream.removeEventListener("scroll", onScroll);
  }, [reduced]);

  React.useEffect(() => {
    const stream = streamRef.current;
    if (!stream || reduced) return;
    let frame = 0;
    let fadeTimer = 0;
    const tick = (now: number) => {
      const previous = lastFrame.current ?? now;
      lastFrame.current = now;
      const atBottom = stream.scrollTop >= stream.scrollHeight - stream.clientHeight - 1;
      if (!paused && now >= manualPauseUntil.current) {
        if (atBottom) {
          bottomSince.current ??= now;
          if (now - bottomSince.current >= 3000 && !restartingRef.current) {
            restartingRef.current = true;
            setRestarting(true);
            fadeTimer = window.setTimeout(() => {
              stream.scrollTop = 0;
              lastAutoTop.current = 0;
              bottomSince.current = null;
              lastFrame.current = null;
              restartingRef.current = false;
              setRestarting(false);
            }, 300);
          }
        } else {
          bottomSince.current = null;
          stream.scrollTop += ((now - previous) * 15) / 1000;
          lastAutoTop.current = stream.scrollTop;
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(fadeTimer);
    };
  }, [paused, reduced]);

  return (
    <div className="adw-stream" ref={streamRef} data-restarting={restarting ? "true" : "false"}>
      {STREAM_GROUPS.map((group, groupIndex) => (
        <section className="adw-stream-group" key={`${group.heading}-${groupIndex}`}>
          <h3>{group.heading}</h3>
          {group.entries.map((entry, entryIndex) => (
            <div className="adw-stream-entry" key={`${entry.time}-${entryIndex}`}>
              <time>{entry.time}</time>
              <p><EmphasizedText text={entry.text} /></p>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function ProgressBlock() {
  return (
    <div className="adw-progress" aria-label="Capital raising progress">
      {PROGRESS_ROWS.map((row) => (
        <div className="adw-progress-row" key={row.label}>
          <span className="adw-progress-label">{row.label}</span>
          <span className="adw-progress-track" aria-hidden="true">
            <span style={{ width: `${(row.completed / row.total) * 100}%` }} />
          </span>
          <span className="adw-progress-figures">{row.figures}</span>
        </div>
      ))}
    </div>
  );
}

export function AgentDartWalkthrough({ variant = "page" }: { variant?: "page" | "home" }) {
  const reduced = useReducedMotion();
  const [paused, setPaused] = React.useState(false);
  return (
    <div
      className={`adw adw-${variant}`}
      data-reduced={reduced ? "true" : "false"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="adw-frame">
        <div className="adw-bar">
          <span className="adw-dots" aria-hidden="true"><i /><i /><i /></span>
          <span className="adw-address">capitaldart.com / deals / agentdart</span>
        </div>
        <div className="adw-plan-label">CAPITAL RAISING PLAN</div>
        <div className="adw-phases" aria-label="Capital raising plan phases">
          {PHASES.map((phase, index) => (
            <div className="adw-phase" data-active={index === 0 ? "true" : "false"} key={phase.number}>
            <div className="adw-phase-copy">
              <span className="adw-phase-num">{phase.number}</span>
              <span className="adw-phase-name">{phase.name}</span>
            </div>
              <span className="adw-phase-track" aria-hidden="true" />
            </div>
          ))}
        </div>
        <div className="adw-stage">
          <ProgressBlock />
          <div className="adw-stream-pane">
            <div className="adw-pane-header">
              <p className="adw-pane-eyebrow">ACTION STREAM</p>
              <h3 className="adw-pane-title">Day 5</h3>
              <p className="adw-pane-subtitle">What AgentDart did today, check by check.</p>
            </div>
            <ActionStream paused={paused} reduced={reduced} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function AgentDartWalkthroughSection() {
  return (
    <section className="section adw-section" aria-labelledby="adw-title">
      <div className="shell">
        <p className="eyebrow">AGENTDART</p>
        <h2 id="adw-title">Day 5 of your raise.</h2>
        <AgentDartWalkthrough variant="page" />
      </div>
    </section>
  );
}
